/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// MUI v4 Switch, NS/Tractie toggle-pendant overrides.
// Tractie source: toggle (composeert switch.css.js + toggle.css.js).
// Tractie switch source verified at:
//   developer-portal-ng/main/app/node_modules/@tractie/components/src/custom-elements/toggle/src/switch.css.js
//
// Tractie spec verified properties (switch.css.js):
//   - dimensions: width 40, height 20 (props lines 30-31)
//   - border-radius: height/2 = 10px = pill (line 19, equivalent --ns-radius-full)
//   - border-width: medium (2px) (line 16)
//   - border-color: transparent default (line 18)
//   - track bg off: var(--tractie-color-background-alpha-default) -> --ns-color-border-emphasis
//     (Tractie computed: rgba(0, 2, 27, 0.23). NS-token --ns-color-border-emphasis
//     === rgb(0 2 27 / 23%). Naam "border-emphasis" is semantisch awkward voor
//     track-bg maar de WAARDE matched Tractie source-of-truth exact.)
//   - track bg on:  var(--tractie-color-background-success-default) -> --ns-color-bg-success-default (verified line 47)
//   - thumb bg:     var(--tractie-color-background-default) -> --ns-color-bg-default (line 15)
//   - motion: NONE (Tractie convention, geen transition declared)
//
// MUI v4 Switch DOM:
//   <span class="MuiSwitch-root">
//     <span class="MuiSwitch-switchBase">
//       <span class="MuiIconButton-label"><input/><span class="MuiSwitch-thumb"/></span>
//     </span>
//     <span class="MuiSwitch-track"/>
//   </span>
//
// MUI defaults that Tractie deviates from:
//   - root size 58x38 -> Tractie 40x20 (different sizing system)
//   - track bg rgba(0,0,0,0.38) -> --ns-color-bg-input (off) / --ns-color-bg-success-default (on)
//   - thumb bg #fafafa -> --ns-color-bg-default
//   - transition 150ms thumb-translate -> Tractie motion: none
//   - colorPrimary checked = primary.main -> Tractie deviation: success-green
//
// Foundation force-* variants (T1.3 pattern): hover en focus-visible krijgen
// `.force-*` class-variants zodat comparison-grid story rows pseudo-states kunnen
// renderen zonder native browser-pseudo-trigger. Active-state is by-design static
// voor Switch (Tractie spec heeft geen pressed-ink op switch host).
//
// Note: MuiSwitch is a special MUI component where root has padding 12px and the
// switchBase positions itself within. We keep MUI's hit-target padding for a11y
// (Tractie spec open question 3 acknowledges 40x20 < 40x40 enhanced WCAG).

import { glyphMask } from '../../icons/glyph-mask';
import { iconCheckXs } from '../../icons/glyphs/check-xs';
import { iconCloseXs } from '../../icons/glyphs/close-xs';

export const MuiSwitchLightOverrides = {
  root: {
    width: 'calc(var(--ns-space-10) + (var(--ns-space-2) * 2))', // 40 + 16 padding = 56
    height: 'calc(var(--ns-space-10) / 2 + (var(--ns-space-2) * 2))', // 20 + 16 = 36
    padding: 'var(--ns-space-2)',
    // Foundation force-focus-visible variant: outline op track (matches BUI
    // outline-rond-indicator pattern voor visuele pariteit). Class is op root.
    '&.force-focus-visible $track': {
      outline:
        'var(--ns-border-width-medium) solid var(--ns-color-border-focus)',
      outlineOffset: 'var(--ns-space-1)',
    },
    '&$disabled': {
      opacity: 0.5,
      pointerEvents: 'none' as const,
    },
  },

  // MuiSwitch-switchBase is the moving thumb-host. Color drives thumb and track-on tint.
  // Bart-advies (2026-05-25): geen margin op thumb, label center in switchBase
  // (MUI default behavior). Hiermee leunen we op MUI's natuurlijke flex-centering
  // ipv handmatige offsets — minder breukpunten bij JSS hashing of root-resize.
  switchBase: {
    padding: 'var(--ns-space-2)',
    color: 'var(--ns-color-bg-default)',
    // switchBase is position:absolute met top:0 binnen root. Default flex-center
    // landt thumb-center op switchBase-center = +12 van switchBase-top vanwege
    // (40-16)/2. Track zit op root-y + 8 (root padding). Thumb-y - track-y zou
    // dan +4 zijn, te laag. Shift switchBase 2px omhoog + 2px naar links zodat
    // flex-centered thumb landt op track-y + 2 / track-x + 2 (matches Tractie
    // grid-cell-start placement).
    top: 'calc(var(--ns-border-width-medium) * -1)',
    left: 'calc(var(--ns-border-width-medium) * -1)',
    '&$checked': {
      // Translate 20 (--ns-space-5) → +20 effectief van label-center-position
      // (label is centered in switchBase, thumb is centered in label).
      transform: 'translateX(var(--ns-space-5))',
      color: 'var(--ns-color-bg-default)',
      '& + $track': {
        backgroundColor: 'var(--ns-color-bg-success-default)',
        borderColor: 'transparent',
        opacity: 1,
      },
    },
    '&:hover, &.force-hover': {
      backgroundColor: 'transparent',
    },
    // Keyboard focus rings the track, as BUI does. MUI v4 Switch has no
    // `focusVisible` style key, so the global Mui-focusVisible class that
    // ButtonBase sets is the selector.
    '&.Mui-focusVisible + $track': {
      outline:
        'var(--ns-border-width-medium) solid var(--ns-color-border-focus)',
      outlineOffset: 'var(--ns-space-1)',
    },
    // switchBase is an IconButton. Its own focus ring would slide out of the
    // root and be clipped by the root's overflow: hidden. The IconButton rule
    // has the same two classes on the same element, so scoping through the
    // root makes this one win whatever order the stylesheets load in.
    '$root &.Mui-focusVisible': {
      outline: 'none',
      boxShadow: 'none',
    },
    // Disabled: align with BUI 0.5 opacity. MUI v4 default switchBase opacity 0.25
    // gives "faded thumb on full-green track" wat onsamenhangend oogt. Tractie
    // [disabled] shadow-DOM toont geen visible change — accepted divergence.
    '&$disabled': {
      opacity: 0.5,
      color: 'var(--ns-color-bg-default)',
    },
  },

  // Q2-aligned: on-state success-green (verified in switch.css.js:47).
  // Tractie-pariteit: GEEN hover-tint op checked-state (MUI v4 default zou
  // alpha(primary.main, hoverOpacity) op switchBase zetten via native :hover,
  // wat als ripple-overlay zichtbaar wordt bij muis-hover. Tractie spec heeft
  // geen :host(:hover), dus we forceren transparent).
  colorPrimary: {
    '&$checked': {
      color: 'var(--ns-color-bg-default)',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '& + $track': {
        backgroundColor: 'var(--ns-color-bg-success-default)',
        borderColor: 'transparent',
        opacity: 1,
      },
    },
  },

  colorSecondary: {
    '&$checked': {
      color: 'var(--ns-color-bg-default)',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '& + $track': {
        backgroundColor: 'var(--ns-color-bg-success-default)',
        borderColor: 'transparent',
        opacity: 1,
      },
    },
  },

  thumb: {
    width: 'calc(var(--ns-space-10) / 2 - (var(--ns-border-width-medium) * 2))', // 20 - 4 = 16
    height:
      'calc(var(--ns-space-10) / 2 - (var(--ns-border-width-medium) * 2))',
    // Geen margin op thumb. Label center-flex (MUI default) zorgt voor
    // horizontal en vertical centering binnen switchBase. Thumb staat dan
    // automatisch in het midden van label, dat in het midden van switchBase zit.
    backgroundColor: 'currentColor' as const,
    boxShadow: 'none',
    // MUI v4 default zet disabled-thumb color naar grey palette. Override via
    // switchBase descendant: thumb erft currentColor van switchBase die wij op
    // bg-default zetten (--ns-color-bg-default = wit).
    '$switchBase$disabled &': {
      backgroundColor: 'var(--ns-color-bg-default)',
    },
  },

  track: {
    // Anchors the icon below. It also makes MUI's z-index -1 on the track take
    // effect, which stays inside the root: the root is positioned with z-index
    // 0, so the track sits behind the thumb and not behind the page.
    position: 'relative' as const,
    width: 'var(--ns-space-10)', // 40px
    height: 'calc(var(--ns-space-10) / 2)', // 20px (pill)
    borderRadius: 'var(--ns-radius-full)',
    // Tractie spec: border-color = transparent (verified switch.css.js line 18).
    // background-clip blijft default border-box (matches Tractie shadow-DOM).
    // Effect: bg-color rendert door transparante border heen → visible pill = 40x20.
    // Thumb 16x16 in 20-tall pill geeft 2px vertical room (matches Tractie's
    // thumb-met-padding visual).
    border: 'var(--ns-border-width-medium) solid transparent',
    backgroundColor: 'var(--ns-color-border-emphasis)',
    opacity: 1,
    // Tractie convention: motion: none (geen transition op state-flips).
    transition: 'none',
    // MUI v4 default disabled-track rules zetten backgroundColor=black + opacity=0.12.
    // Override met onze tokens + 0.5 opacity (align met BUI). Selectors moeten exact
    // matchen wat MUI gebruikt: `$switchBase$disabled + &` (unchecked-disabled) en
    // `$switchBase$checked$disabled + &` (checked-disabled).
    '$switchBase$disabled + &': {
      backgroundColor: 'var(--ns-color-border-emphasis)',
      borderColor: 'transparent',
      opacity: 0.5,
    },
    '$switchBase$checked$disabled + &': {
      backgroundColor: 'var(--ns-color-bg-success-default)',
      borderColor: 'transparent',
      opacity: 0.5,
    },
    // Tractie draws a close in the free half of the track, and a check once it
    // is on (TRACTIE Toggle, Figma 2393:10534: 16 by 16, x=20 off and x=4 on in
    // the 40 wide track, so 18 and 2 inside its border of 2).
    '&::before': {
      content: '""',
      position: 'absolute' as const,
      top: 0,
      left: 'calc(var(--ns-space-5) - var(--ns-border-width-medium))',
      width: 'var(--ns-space-4)',
      height: 'var(--ns-space-4)',
      backgroundColor: 'var(--ns-color-fg-default-inverse)',
      maskImage: glyphMask(iconCloseXs),
      maskSize: '100% 100%',
    },
    '$switchBase$checked + &::before': {
      left: 'calc(var(--ns-space-1) - var(--ns-border-width-medium))',
      maskImage: glyphMask(iconCheckXs),
    },
  },

  checked: {},
  disabled: {},
} as const;

// Dark spreads Light: token-driven (success-cluster + bg-default mode-flippen via tokens.css).
export const MuiSwitchDarkOverrides = {
  ...MuiSwitchLightOverrides,
} as const;
